import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Divider, Grid, Heading, HStack, Select, Text, VStack, Box } from '@chakra-ui/react';
import { getRoutingConfigs, updateRoutingConfigs, getRoutingConfigMeta } from '@store/actions/rdaDashboardActions';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE } from '@constants';
import InputComponent from '@components/Input';
import { MdOutlineAdd, MdOutlineDelete, MdDragIndicator } from 'react-icons/md';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const RoutingConfigDashboard = () => {
  const dispatch = useDispatch();
  const { routingConfigs, routingConfigMeta } = useSelector((state) => state.rdaDashboard);
  const [updatedConfigs, setUpdatedConfigs] = useState([]);
  const [meta, setMeta] = useState({ partners: [], transferTypes: [] });
  const [newConfig, setNewConfig] = useState({
    partner_name: '',
    rail: '',
    priority: '',
    amount_min_limit: '',
    amount_max_limit: ''
  });
  const [toReview, setToReview] = useState(false);

  useEffect(() => {
    dispatch(getRoutingConfigs());
    dispatch(getRoutingConfigMeta());
  }, [dispatch]);

  useEffect(() => {
    if (routingConfigs.data) setUpdatedConfigs(routingConfigs.data);
  }, [routingConfigs.data]);

  useEffect(() => {
    if (routingConfigMeta.data) setMeta(routingConfigMeta.data);
  }, [routingConfigMeta.data]);

  const handleReview = () => setToReview(!toReview);

  const addConfig = () => {
    const { partner_name, rail, priority } = newConfig;
    if (!partner_name || !rail || priority === '') {
      dispatch(setAlert({ title: 'All fields are required', status: ALERT_STATUS_TYPE.ERROR }));
      return;
    }
    if (updatedConfigs.some(cfg => cfg.partner_name === partner_name && cfg.rail === rail)) {
      dispatch(setAlert({ title: 'Duplicate entry', status: ALERT_STATUS_TYPE.ERROR }));
      return;
    }
    setUpdatedConfigs([...updatedConfigs, { ...newConfig }]);
    setNewConfig({ partner_name: '', rail: '', priority: '', amount_min_limit: '', amount_max_limit: '' });
  };

  const handleRemove = (partner_name, rail) => {
    setUpdatedConfigs(updatedConfigs.filter((item) => !(item.partner_name === partner_name && item.rail === rail)));
  };

  const handleUpdate = () => {
    for (const cfg of updatedConfigs) {
      if (cfg.priority < 0) {
        dispatch(setAlert({ title: 'Priority cannot be negative', status: ALERT_STATUS_TYPE.ERROR }));
        return;
      }
      if (cfg.amount_min_limit && cfg.amount_max_limit && Number(cfg.amount_min_limit) > Number(cfg.amount_max_limit)) {
        dispatch(setAlert({ title: 'Min limit cannot exceed max limit', status: ALERT_STATUS_TYPE.ERROR }));
        return;
      }
    }
    dispatch(updateRoutingConfigs(updatedConfigs, () => {
      dispatch(getRoutingConfigs());
      setToReview(false);
    }));
  };

  // Drag and drop logic
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(updatedConfigs);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    // Update priorities based on new order
    setUpdatedConfigs(reordered.map((cfg, idx) => ({ ...cfg, priority: idx })));
  };

  const handleUpdateConfig = (name, value, partner_name, rail) => {
    setUpdatedConfigs((prevState) =>
      prevState.map((item) =>
        item.partner_name === partner_name && item.rail === rail
          ? { ...item, [name]: value }
          : item
      )
    );
  };

  return (
    <VStack alignItems={'flex-start'} gap={4} px={2}>
      <Heading size={'md'} color={'white'} mt={2} mb={2}>
        Manage Routing Configs
      </Heading>
      {!toReview && (
        <>
          <VStack alignItems={'stretch'} gap={2} w={'full'}>
            <Heading size={'sm'}>Add Routing Config</Heading>
            <Grid
              w={'full'}
              gridTemplateColumns={'2fr 2fr 1fr 1fr 1fr 1fr'}
              alignItems={'center'}
              gap={4}
            >
              <Select
                name={'partner_name'}
                placeholder={'Select Partner Name'}
                value={newConfig.partner_name}
                onChange={e => setNewConfig({ ...newConfig, partner_name: e.target.value })}
                border={'none'}
                borderRadius={'xl'}
                backgroundColor={'#414141'}
                focusBorderColor={'brand.400'}
                color={'white'}
              >
                {meta.partners?.map((partner) => (
                  <option key={partner} value={partner}>{partner}</option>
                ))}
              </Select>
              <Select
                name={'rail'}
                placeholder={'Select Transfer Type'}
                value={newConfig.rail}
                onChange={e => setNewConfig({ ...newConfig, rail: e.target.value })}
                border={'none'}
                borderRadius={'xl'}
                backgroundColor={'#414141'}
                focusBorderColor={'brand.400'}
                color={'white'}
              >
                {meta.transferTypes?.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Select>
              <InputComponent
                name={'priority'}
                placeholder={'Priority'}
                width="100%"
                handleChange={e => setNewConfig({ ...newConfig, priority: e.target.value })}
                size={'md'}
                value={newConfig.priority}
              />
              <InputComponent
                name={'amount_min_limit'}
                placeholder={'Min Limit'}
                width="100%"
                handleChange={e => setNewConfig({ ...newConfig, amount_min_limit: e.target.value })}
                size={'md'}
                value={newConfig.amount_min_limit}
              />
              <InputComponent
                name={'amount_max_limit'}
                placeholder={'Max Limit'}
                width="100%"
                handleChange={e => setNewConfig({ ...newConfig, amount_max_limit: e.target.value })}
                size={'md'}
                value={newConfig.amount_max_limit}
              />
              <Button
                w={'fit-content'}
                borderRadius={'xl'}
                colorScheme="brand"
                color={'black'}
                rightIcon={<MdOutlineAdd />}
                onClick={addConfig}
              >
                Add
              </Button>
            </Grid>
          </VStack>
          <Divider />
        </>
      )}
      <VStack alignItems={'stretch'} gap={2} width={'100%'}>
        <Heading size={'sm'}>
          {!toReview ? 'Update Routing Configs' : 'Review Routing Configs'}
        </Heading>
        <VStack
          border={'2px solid'}
          borderColor={'whiteAlpha.500'}
          borderRadius={'xl'}
          width={'100%'}
          overflowX={{ base: 'auto', md: 'unset' }}
        >
          <Grid
            gap={2}
            p={2}
            gridTemplateColumns={{ base: '1fr', md: toReview ? '2fr 2fr 1fr 1fr 1fr 1fr' : '40px 2fr 2fr 1fr 1fr 1fr 1fr' }}
            alignItems={'center'}
            w={'full'}
            borderBottom={'2px solid'}
            borderColor={'whiteAlpha.500'}
          >
            {!toReview && <Text fontSize={'sm'} color={'gray'} textAlign={'center'}> </Text>}
            <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>Partner Name</Text>
            <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>Transfer Type</Text>
            <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>Priority</Text>
            <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>Min Limit</Text>
            <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>Max Limit</Text>
            <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>Action</Text>
          </Grid>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="routing-configs" isDropDisabled={toReview}>
              {(provided) => (
                <VStack py={2} width={'100%'} spacing={1} ref={provided.innerRef} {...provided.droppableProps}>
                  {updatedConfigs.length > 0 ? (
                    updatedConfigs.map((data, id) => (
                      <Draggable key={id} draggableId={String(id)} index={id} isDragDisabled={toReview}>
                        {(provided) => (
                          <Grid
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            gap={2}
                            p={2}
                            gridTemplateColumns={{ base: '1fr', md: toReview ? '2fr 2fr 1fr 1fr 1fr 1fr' : '40px 2fr 2fr 1fr 1fr 1fr 1fr' }}
                            alignItems={'center'}
                            w={'full'}
                          >
                            {!toReview && (
                              <Box display="flex" alignItems="center" justifyContent="center" cursor="grab" color="whiteAlpha.600">
                                <MdDragIndicator size={20} />
                              </Box>
                            )}
                            <Text fontSize={'xs'} color={'white'} wordBreak={'break-word'} textAlign="center" display="flex" justifyContent="center" alignItems="center">{data.partnerName}</Text>
                            <Text fontSize={'xs'} color={'white'} wordBreak={'break-word'} textAlign="center" display="flex" justifyContent="center" alignItems="center">{data.rail}</Text>
                            <Text fontSize={'xs'} color={'white'} wordBreak={'break-word'} textAlign="center" display="flex" justifyContent="center" alignItems="center">{data.priority}</Text>
                            <Text fontSize={'xs'} color={'white'} wordBreak={'break-word'} textAlign="center" display="flex" justifyContent="center" alignItems="center">{data.amountMinLimit}</Text>
                            <Text fontSize={'xs'} color={'white'} wordBreak={'break-word'} textAlign="center" display="flex" justifyContent="center" alignItems="center">{data.amountMaxLimit}</Text>
                            <Button
                              width={'1rem'}
                              colorScheme={'red'}
                              size="sm"
                              bgColor="whiteAlpha.300"
                              border="1px solid"
                              borderColor="whiteAlpha.300"
                              borderRadius="full"
                              p={0}
                              mx={'auto'}
                              aria-label={`Delete ${data.partner_name}`}
                              onClick={() => handleRemove(data.partner_name, data.rail)}
                            >
                              <MdOutlineDelete size={'1rem'} />
                            </Button>
                          </Grid>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <Text py={2} color={'whiteAlpha.900'}>
                      No Routing Configs Available
                    </Text>
                  )}
                  {provided.placeholder}
                </VStack>
              )}
            </Droppable>
          </DragDropContext>
        </VStack>
      </VStack>
      <HStack w="full" justifyContent={'flex-end'} my={4}>
        <Button
          colorScheme="brand"
          onClick={handleReview}
          variant={'outline'}
        >
          {toReview ? 'Edit' : 'Update'}
        </Button>
        {toReview && (
          <Button color={'black'} colorScheme="brand" onClick={handleUpdate}>
            Update
          </Button>
        )}
      </HStack>
    </VStack>
  );
};

export default RoutingConfigDashboard; 